import { useState, useEffect } from "react"
import FournisseurCard from "../../elements/fournisseurs_card"
import FournisseurList from "../../elements/fournisseurs_list"
import {Link} from "react-router-dom"; 
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import Loading from "../../cors/Loading";
import { toast } from 'sonner';

export default function FournisseursPage() {
  const { t } = useLanguage();
  const { token, role, roles } = useAuth();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("name");
  const [showSortDropdown, setShowSortDropdown] = useState(false); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFournisseurs = async () => {
    setError(null);
    try {
      const response = await fetch(BASE_URL + '/api/fournisseurs', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(t('fournisseurs.error_fetch_msg', { status: response.statusText }));
      }
      const data = await response.json();

      if (Array.isArray(data)) {
        setFournisseurs(data);
      } else if (data && Array.isArray(data.fournisseurs)) {
        setFournisseurs(data.fournisseurs);
      } else if (data && Array.isArray(data.data)) {
        setFournisseurs(data.data);
      } else {
        console.error("API response for fournisseurs is not an array:", data);
        setFournisseurs([]);
        toast.error(t('fournisseurs.error_unexpected_format'));
      }

    } catch (err) {
      setError(err.message);
      toast.error(err.message || t('fournisseurs.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFournisseurs();
  }, []);

  const filteredFournisseurs = fournisseurs
    .filter((fournisseur) => {
      const matchesSearch =
        fournisseur.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fournisseur.name_personnel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fournisseur.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fournisseur.phone?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "name_personnel":
          return a.name_personnel ? a.name_personnel.localeCompare(b.name_personnel || '') : 0;
        case "newest":
          return new Date(b.created_at) - new Date(a.created_at);
        case "oldest":
          return new Date(a.created_at) - new Date(b.created_at);
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5 max">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('fournisseurs.title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('fournisseurs.subtitle')}
            </p>
          </div>
          {
            (role === 1 || (role === 2 && roles.add_fournisseur === 1)) &&
              <div className="mt-6 md:mt-0">
                <Link to="/fournisseurs/create">
                  <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                    <span className="flex items-center">
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('fournisseurs.add')}
                    </span>
                  </button>
                </Link>
              </div>
          }
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('fournisseurs.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Controls */}
            <div className="flex gap-4 max-xl:w-full">
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex dark:bg-stone-900 cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs dark:text-gray-300">{t('common.filters')}</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs ">
                      {[
                        { value: "name", label: t('fournisseurs.sort_name') },
                        { value: "name_personnel", label: t('fournisseurs.sort_contact') },
                        { value: "newest", label: t('fournisseurs.sort_newest') },
                        { value: "oldest", label: t('fournisseurs.sort_oldest') },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "grid" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "list" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading and error feedback */}
        {loading &&
          <div className="flex justify-center text-[var(--color-primary-500)] text-5xl mt-40">
            <Loading />
          </div>
        }
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
                {filteredFournisseurs.length} {filteredFournisseurs.length === 1 ? t('fournisseurs.fournisseur_singular') : t('fournisseurs.fournisseur_plural')} {t('fournisseurs.found')}
              </span>
            </div>

            {/* Fournisseurs Display */}
            {filteredFournisseurs.length > 0 ? (
              viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {filteredFournisseurs.map((fournisseur) => (
                    <FournisseurCard key={fournisseur.id} fournisseur={fournisseur} fetchFournisseurs={fetchFournisseurs} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredFournisseurs.map((fournisseur) => (
                    <FournisseurList key={fournisseur.id} fournisseur={fournisseur} fetchFournisseurs={fetchFournisseurs} />
                  ))}
                </div>
              )
            ) : (
              /* Empty State */
              <div className="text-center py-10">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2m7-12a4 4 0 100-8 4 4 0 000 8zm7 0a4 4 0 100-8 4 4 0 000 8zm-7 0a4 4 0 100-8 4 4 0 000 8z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('fournisseurs.not_found')}</h3>
                <p className="text-gray-600 text-sm mb-6">
                  {t('fournisseurs.not_found_desc')}
                </p>
                {
                  (role === 1 || (role === 2 && roles.add_fournisseur === 1)) &&
                    <Link to="/fournisseurs/create">
                      <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                        {t('fournisseurs.create_first')}
                      </button>
                    </Link>
                }
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}